import React, { useState, useEffect } from "react";
import { getSelfSignedDocuments, deleteSelfSignedDocument } from "../api/user";
import toast from "react-hot-toast";
import {
  DocumentIcon,
  CloudArrowUpIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import UploadDocumentModal from "../components/UploadDocumentModal";

const SelfSign = () => {
  const [selfSignedDocuments, setSelfSignedDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [viewMode] = useState("grid"); // 'grid' or 'list'
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    document: null,
    isLoading: false,
  });
  const [uploadModal, setUploadModal] = useState(false);

  // Load self-signed documents on component mount
  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setLoadingDocuments(true);
        const response = await getSelfSignedDocuments();
        setSelfSignedDocuments(response.data?.documents || []);
      } catch (error) {
        console.error("Error loading documents:", error);
        toast.error("Failed to load documents");
        // Fallback to empty array
        setSelfSignedDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, []);

  const handleUploadSuccess = async (uploadedDocument) => {
    // Reload documents list
    try {
      const documentsResponse = await getSelfSignedDocuments();
      setSelfSignedDocuments(documentsResponse.data?.documents || []);
    } catch (error) {
      console.error("Error reloading documents:", error);
    }
  };

  const handleDocumentClick = (document) => {
    // Navigate to SelfSignDocument with existing file
    window.open(
      `${process.env.REACT_APP_FILE_URL}/${document.uploadUrl.replace(
        /\\/g,
        "/"
      )}`,
      "_blank"
    );
  };

  const convertBytesToMB = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    if (bytes < 1024) return bytes + " Bytes";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1024 / 1024).toFixed(2) + " MB";
  };

  const handleDeleteClick = (document) => {
    setDeleteModal({
      isOpen: true,
      document: document,
      isLoading: false,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.document) return;

    setDeleteModal((prev) => ({ ...prev, isLoading: true }));

    try {
      await deleteSelfSignedDocument(deleteModal.document._id);

      // Remove the document from the local state
      setSelfSignedDocuments((prev) =>
        prev.filter((doc) => doc._id !== deleteModal.document._id)
      );

      toast.success("Document deleted successfully");
      setDeleteModal({ isOpen: false, document: null, isLoading: false });
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error(error.message || "Failed to delete document");
      setDeleteModal((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, document: null, isLoading: false });
  };

  const handleDownload = async (document) => {
    try {
      // Create the full URL for the document
      const fileUrl = `${
        process.env.REACT_APP_FILE_URL
      }/${document.uploadUrl.replace(/\\/g, "/")}`;

      // Fetch the file
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      // Convert response to blob
      const blob = await response.blob();

      // / Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.fileName || "document.pdf";
      window.document.body.appendChild(link);
      link.click();

      // Clean up
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8 w-full">
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Self Sign</h1>
            <p className="text-gray-600">
              Upload documents and sign them yourself. Perfect for personal
              documents, contracts, and forms.
            </p>
          </div>
          <button
            onClick={() => setUploadModal(true)}
            className="bg-c-blue text-white px-6 py-2 rounded-lg transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Documents List */}
      <div className="">
        {loadingDocuments ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading documents...</p>
          </div>
        ) : selfSignedDocuments.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">No documents yet</p>
            <p className="text-sm text-gray-400">
              Upload your first document to get started
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                : "divide-y divide-gray-200"
            }
          >
            {selfSignedDocuments.map((document) => (
              <div
                key={document._id}
                className={`group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary/20 ${
                  viewMode === "grid"
                    ? "hover:scale-[1.02] transform cursor-pointer"
                    : "p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                }`}
                onClick={() => handleDocumentClick(document)}
              >
                <div className={viewMode === "grid" ? "p-4" : "p-0"}>
                  <div
                    className={`flex justify-between items-center ${
                      viewMode === "grid" ? "mb-2" : "mb-0"
                    }`}
                  >
                    <div
                      className={
                        viewMode === "grid"
                          ? ""
                          : "flex-1 flex items-center space-x-4"
                      }
                    >
                      <h3
                        className={`font-semibold text-gray-900 group-hover:text-primary transition-colors duration-200 ${
                          viewMode === "grid"
                            ? "text-lg truncate max-w-[180px]"
                            : "text-base"
                        }`}
                      >
                        {document.fileName}
                      </h3>
                      {viewMode === "list" && (
                        <p className="text-sm text-gray-500 line-clamp-1 max-w-xs">
                          Uploaded on{" "}
                          {new Date(document.createdAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDocumentClick(document);
                        }}
                        className="text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg p-1.5 transition-all duration-200"
                        title="View Document"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(document);
                        }}
                        className="text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg p-1.5 transition-all duration-200"
                        title="Download Document"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(document);
                        }}
                        className="text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg p-1.5 transition-all duration-200"
                        title="Delete Document"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {viewMode === "grid" && (
                    <p className="text-sm text-gray-600 line-clamp-1 mb-3 leading-relaxed">
                      Uploaded on{" "}
                      {new Date(document.createdAt).toLocaleDateString()}
                    </p>
                  )}

                  <div
                    className={`${
                      viewMode === "grid"
                        ? "space-y-2"
                        : "flex items-center space-x-4"
                    }`}
                  >
                    <div
                      className={`flex items-center text-sm text-gray-500 ${
                        viewMode === "list"
                          ? "flex-1"
                          : "rounded-lg px-2 py-1.5"
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="800px"
                        height="800px"
                        viewBox="-4 0 40 40"
                        fill="none"
                        className="h-[20px] w-[20px] text-gray-400 mr-2"
                      >
                        <path
                          d="M25.6686 26.0962C25.1812 26.2401 24.4656 26.2563 23.6984 26.145C22.875 26.0256 22.0351 25.7739 21.2096 25.403C22.6817 25.1888 23.8237 25.2548 24.8005 25.6009C25.0319 25.6829 25.412 25.9021 25.6686 26.0962ZM17.4552 24.7459C17.3953 24.7622 17.3363 24.7776 17.2776 24.7939C16.8815 24.9017 16.4961 25.0069 16.1247 25.1005L15.6239 25.2275C14.6165 25.4824 13.5865 25.7428 12.5692 26.0529C12.9558 25.1206 13.315 24.178 13.6667 23.2564C13.9271 22.5742 14.193 21.8773 14.468 21.1894C14.6075 21.4198 14.7531 21.6503 14.9046 21.8814C15.5948 22.9326 16.4624 23.9045 17.4552 24.7459ZM14.8927 14.2326C14.958 15.383 14.7098 16.4897 14.3457 17.5514C13.8972 16.2386 13.6882 14.7889 14.2489 13.6185C14.3927 13.3185 14.5105 13.1581 14.5869 13.0744C14.7049 13.2566 14.8601 13.6642 14.8927 14.2326ZM9.63347 28.8054C9.38148 29.2562 9.12426 29.6782 8.86063 30.0767C8.22442 31.0355 7.18393 32.0621 6.64941 32.0621C6.59681 32.0621 6.53316 32.0536 6.44015 31.9554C6.38028 31.8926 6.37069 31.8476 6.37359 31.7862C6.39161 31.4337 6.85867 30.8059 7.53527 30.2238C8.14939 29.6957 8.84352 29.2262 9.63347 28.8054ZM27.3706 26.1461C27.2889 24.9719 25.3123 24.2186 25.2928 24.2116C24.5287 23.9407 23.6986 23.8091 22.7552 23.8091C21.7453 23.8091 20.6565 23.9552 19.2582 24.2819C18.014 23.3999 16.9392 22.2957 16.1362 21.0733C15.7816 20.5332 15.4628 19.9941 15.1849 19.4675C15.8633 17.8454 16.4742 16.1013 16.3632 14.1479C16.2737 12.5816 15.5674 11.5295 14.6069 11.5295C13.948 11.5295 13.3807 12.0175 12.9194 12.9813C12.0965 14.6987 12.3128 16.8962 13.562 19.5184C13.1121 20.5751 12.6941 21.6706 12.2895 22.7311C11.7861 24.0498 11.2674 25.4103 10.6828 26.7045C9.04334 27.3532 7.69648 28.1399 6.57402 29.1057C5.8387 29.7373 4.95223 30.7028 4.90163 31.7107C4.87693 32.1854 5.03969 32.6207 5.37044 32.9695C5.72183 33.3398 6.16329 33.5348 6.6487 33.5354C8.25189 33.5354 9.79489 31.3327 10.0876 30.8909C10.6767 30.0029 11.2281 29.0124 11.7684 27.8699C13.1292 27.3781 14.5794 27.011 15.985 26.6562L16.4884 26.5283C16.8668 26.4321 17.2601 26.3257 17.6635 26.2153C18.0904 26.0999 18.5296 25.9802 18.976 25.8665C20.4193 26.7844 21.9714 27.3831 23.4851 27.6028C24.7601 27.7883 25.8924 27.6807 26.6589 27.2811C27.3486 26.9219 27.3866 26.3676 27.3706 26.1461ZM30.4755 36.2428C30.4755 38.3932 28.5802 38.5258 28.1978 38.5301H3.74486C1.60224 38.5301 1.47322 36.6218 1.46913 36.2428L1.46884 3.75642C1.46884 1.6039 3.36763 1.4734 3.74457 1.46908H20.263L20.2718 1.4778V7.92396C20.2718 9.21763 21.0539 11.6669 24.0158 11.6669H30.4203L30.4753 11.7218L30.4755 36.2428ZM28.9572 10.1976H24.0169C21.8749 10.1976 21.7453 8.29969 21.7424 7.92417V2.95307L28.9572 10.1976ZM31.9447 36.2428V11.1157L21.7424 0.871022V0.823357H21.6936L20.8742 0H3.74491C2.44954 0 0 0.785336 0 3.75711V36.2435C0 37.5427 0.782956 40 3.74491 40H28.2001C29.4952 39.9997 31.9447 39.2143 31.9447 36.2428Z"
                          fill="#EB5757"
                        />
                      </svg>
                      <p className="truncate">{document.fileName}</p>
                    </div>

                    <div
                      className={`flex flex-wrap gap-1.5 ${
                        viewMode === "list" ? "flex-shrink-0" : ""
                      }`}
                    >
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {convertBytesToMB(document.fileSize)}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className={`${
                    viewMode === "grid"
                      ? "bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4"
                      : "px-4 py-3"
                  } flex justify-between items-center border-t border-gray-100`}
                >
                  <div
                    className={`text-gray-500 font-medium ${
                      viewMode === "grid" ? "text-sm" : "text-base"
                    }`}
                  >
                    {viewMode === "grid" ? "PDF Document" : "PDF"}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {new Date(document.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        documentName={deleteModal.document?.fileName}
        isLoading={deleteModal.isLoading}
      />

      <UploadDocumentModal
        isOpen={uploadModal}
        onClose={() => setUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default SelfSign;
